import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import { changePassword } from '../services/api';
import Footer from 'components/PageFooter';

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const history = useHistory();
  const [darkMode, setDarkMode] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    // Load dark mode preference from localStorage
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', String(newDarkMode));
    
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleLogout = () => {
    logout();
    history.push('/');
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    
    if (!currentPassword || !newPassword) {
      setPasswordError('Please fill in all fields');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    setPasswordLoading(true);

    try {
      await changePassword(currentPassword, newPassword);
      alert('Password changed successfully. You will now be logged out.');
      logout();
      history.push('/');
    } catch (error) {
      const err = error;
      setPasswordError((err && err.response && err.response.data && err.response.data.message) || 'Failed to change password');
      setPasswordLoading(false);
    }
  };

  const handleCancelChangePassword = () => {
    setShowChangePassword(false);
    setCurrentPassword('');
    setNewPassword('');
    setPasswordError('');
  };

  // Custom buttons for Settings page
  const customButtons = [
    <button 
      key="back"
      onClick={() => history.goBack()} 
      className="btn-secondary"
    >
      {t('common.back')}
    </button>,
    <button 
      key="logout"
      onClick={handleLogout} 
      className="btn-primary"
    >
      {t('common.logout')}
    </button>
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Navigation Header */}
      <PageHeader additionalButtons={customButtons} />

      {/* Main Content */}
      <div className="flex-1 w-full max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{t('settings.title')}</h2>
          <p className="text-gray-600 dark:text-gray-400">{t('settings.subtitle')}</p>
        </div>

        {/* Settings Sections */}
        <div className="space-y-6">
          {/* Account Information */}
          <div className="card">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">{t('settings.accountInfo')}</h3>
            <div className="space-y-4">
              <div>
                <label className="form-label">{t('settings.username')}</label>
                <input
                  type="text"
                  value={user?.username || ''}
                  disabled
                  className="input-field bg-gray-100 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="form-label">{t('settings.role')}</label>
                <input
                  type="text"
                  value={user?.role || 'user'}
                  disabled
                  className="input-field bg-gray-100 cursor-not-allowed"
                />
              </div>
              {user?.role === 'client' && (
                <div>
                  <label className="form-label">{t('settings.yourClientId')}</label>
                  <input
                    type="text"
                    value={user?.clientId || 'N/A'}
                    disabled
                    className="input-field bg-gray-100 cursor-not-allowed"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Preferences */}
          <div className="card">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">{t('settings.preferences')}</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{t('settings.darkMode')}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('settings.darkModeDesc')}</p>
                </div>
                <button
                  onClick={toggleDarkMode}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    darkMode ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      darkMode ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="card">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Security</h3>
            <div className="space-y-4">
              {!showChangePassword ? (
                <button 
                  onClick={() => setShowChangePassword(true)}
                  className="btn-secondary w-full text-left"
                >
                  Change Password
                </button>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Change Password</h4>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Current Password
                      </label>
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="input-field"
                        placeholder="Enter current password"
                        disabled={passwordLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        New Password
                      </label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="input-field"
                        placeholder="Enter new password (min 6 characters)"
                        disabled={passwordLoading}
                      />
                    </div>
                    {passwordError && (
                      <div className="text-red-600 dark:text-red-400 text-sm">
                        {passwordError}
                      </div>
                    )}
                    <div className="flex space-x-3">
                      <button 
                        type="submit" 
                        className="btn-primary"
                        disabled={passwordLoading}
                      >
                        {passwordLoading ? 'Changing...' : 'Change Password'}
                      </button>
                      <button 
                        type="button" 
                        onClick={handleCancelChangePassword}
                        className="btn-secondary"
                        disabled={passwordLoading}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Settings;